import React, { useState, useMemo } from 'react';
import { Search, MoreVertical, UserPlus, Shield, DollarSign, MessageSquare, Trophy } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatsCard } from '@/components/shared/StatsCard';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { formatDistanceToNow } from 'date-fns';
import { mockUsers } from '@/data/mock';
import { toast } from 'sonner';

const TIER_COLORS = {
  'Starter': 'bg-slate-100 text-slate-700',
  'Trader': 'bg-blue-100 text-blue-700',
  'Pro Trader': 'bg-purple-100 text-purple-700',
  'Elite Trader': 'bg-amber-100 text-amber-700',
};

export const UsersPage = () => {
  const [users, setUsers] = useState(mockUsers);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editRoleUser, setEditRoleUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [adjustPointsUser, setAdjustPointsUser] = useState(null);
  const [pointsAmount, setPointsAmount] = useState('');
  const [pointsReason, setPointsReason] = useState('');
  const [deleteUser, setDeleteUser] = useState(null);
  const [editEmailUser, setEditEmailUser] = useState(null);
  const [newEmail, setNewEmail] = useState('');

  const filteredUsers = useMemo(() => {
    return users.filter((user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [users, searchTerm]);

  const activeUsers = useMemo(() => users.filter(u => u.status === 'active').length, [users]);
  const suspendedUsers = useMemo(() => users.filter(u => u.status === 'suspended').length, [users]);
  const newThisWeek = useMemo(() => {
    return users.filter(u => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return new Date(u.joined) > weekAgo;
    }).length;
  }, [users]);

  return (
    <div>
      <PageHeader
        title="Users"
        description="Manage user accounts and permissions"
        breadcrumbs={['Admin', 'Community', 'Users']}
      />

      {/* Stats Row */}
      <div className="grid gap-6 md:grid-cols-4 mb-6">
        <StatsCard
          title="Total Users"
          value={users.length}
          icon={UserPlus}
          iconBg="bg-blue-100 text-blue-600"
        />
        <StatsCard
          title="New This Week"
          value={newThisWeek}
          icon={Trophy}
          iconBg="bg-purple-100 text-purple-600"
        />
        <StatsCard
          title="Active"
          value={activeUsers}
          icon={Shield}
          iconBg="bg-emerald-100 text-emerald-600"
        />
        <StatsCard
          title="Suspended"
          value={suspendedUsers}
          icon={Shield}
          iconBg="bg-red-100 text-red-600"
        />
      </div>

      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardContent className="p-6">
          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Table */}
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    User
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Role
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Tier
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">
                    Reviews
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">
                    Points
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Joined
                  </TableHead>
                  <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Status
                  </TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id} className="hover:bg-slate-50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={user.avatar} />
                          <AvatarFallback>{user.name[0]}</AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {user.name}
                          </p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize">
                        {user.role.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={TIER_COLORS[user.tier]}>
                        {user.tier}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm font-medium text-slate-900">
                        {user.reviewCount}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="text-sm font-medium text-slate-900">
                        {user.points.toLocaleString()}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-slate-600">
                        {formatDistanceToNow(new Date(user.joined), { addSuffix: true })}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className={`h-2 w-2 rounded-full ${
                            user.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'
                          }`}
                        />
                        <span className="text-sm capitalize">{user.status}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setSelectedUser(user)}>
                            View Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setEditEmailUser(user); setNewEmail(user.email); }}>
                            Edit Email
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setEditRoleUser(user); setNewRole(user.role); }}>Change Role</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setAdjustPointsUser(user)}>Adjust Points</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setUsers(users.map(u =>
                                u.id === user.id
                                  ? { ...u, status: u.status === 'active' ? 'suspended' : 'active' }
                                  : u
                              ));
                              toast.success(
                                user.status === 'active'
                                  ? 'User suspended'
                                  : 'User activated'
                              );
                            }}
                          >
                            {user.status === 'active' ? 'Suspend' : 'Activate'}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => setDeleteUser(user)}
                          >
                            Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* User Detail Sheet */}
      <Sheet open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <SheetContent className="w-[480px] overflow-y-auto">
          {selectedUser && (
            <>
              <SheetHeader>
                <SheetTitle>User Profile</SheetTitle>
              </SheetHeader>
              <div className="mt-6 space-y-6">
                <div className="flex items-center gap-4">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={selectedUser.avatar} />
                    <AvatarFallback className="text-2xl">
                      {selectedUser.name[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900">
                      {selectedUser.name}
                    </h3>
                    <p className="text-sm text-slate-600">{selectedUser.email}</p>
                    <Badge className={`mt-2 ${TIER_COLORS[selectedUser.tier]}`}>
                      {selectedUser.tier}
                    </Badge>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <MessageSquare className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                      <p className="text-2xl font-bold text-slate-900">
                        {selectedUser.reviewCount}
                      </p>
                      <p className="text-xs text-slate-600">Reviews</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Trophy className="h-6 w-6 mx-auto mb-2 text-amber-600" />
                      <p className="text-2xl font-bold text-slate-900">
                        {selectedUser.points.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-600">Points</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <DollarSign className="h-6 w-6 mx-auto mb-2 text-emerald-600" />
                      <p className="text-2xl font-bold text-slate-900">
                        {selectedUser.verifiedPayouts}
                      </p>
                      <p className="text-xs text-slate-600">Payouts</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <Shield className="h-6 w-6 mx-auto mb-2 text-purple-600" />
                      <p className="text-sm font-medium text-slate-900 capitalize">
                        {selectedUser.status}
                      </p>
                      <p className="text-xs text-slate-600">Status</p>
                    </CardContent>
                  </Card>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-2">ACCOUNT INFO</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Joined</span>
                      <span className="font-medium">
                        {new Date(selectedUser.joined).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Last Active</span>
                      <span className="font-medium">
                        {formatDistanceToNow(new Date(selectedUser.lastActive), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Role</span>
                      <span className="font-medium capitalize">
                        {selectedUser.role.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button className="flex-1">Edit User</Button>
                  <Button variant="outline" className="flex-1">
                    Adjust Points
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Change Role Dialog */}
      <Dialog open={!!editRoleUser} onOpenChange={() => setEditRoleUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>User</Label>
              <p className="text-sm font-medium text-slate-900 mt-2">{editRoleUser?.name}</p>
            </div>
            <div>
              <Label>New Role</Label>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              className="w-full"
              onClick={() => {
                setUsers(users.map(u => u.id === editRoleUser.id ? { ...u, role: newRole } : u));
                toast.success('Role updated successfully');
                setEditRoleUser(null);
              }}
            >
              Update Role
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Adjust Points Dialog */}
      <Dialog open={!!adjustPointsUser} onOpenChange={() => { setAdjustPointsUser(null); setPointsAmount(''); setPointsReason(''); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Points</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>User</Label>
              <p className="text-sm font-medium text-slate-900 mt-2">{adjustPointsUser?.name}</p>
              <p className="text-xs text-slate-500">Current Points: {adjustPointsUser?.points.toLocaleString()}</p>
            </div>
            <div>
              <Label>Points Amount (use + or - for add/subtract)</Label>
              <Input
                type="text"
                placeholder="+100 or -50"
                value={pointsAmount}
                onChange={(e) => setPointsAmount(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea
                placeholder="Why are you adjusting points?"
                value={pointsReason}
                onChange={(e) => setPointsReason(e.target.value)}
                rows={3}
                className="mt-2"
              />
            </div>
            <Button
              className="w-full"
              onClick={() => {
                const amount = parseInt(pointsAmount);
                if (isNaN(amount)) {
                  toast.error('Please enter a valid number');
                  return;
                }
                if (!pointsReason.trim()) {
                  toast.error('Please provide a reason');
                  return;
                }
                setUsers(users.map(u => 
                  u.id === adjustPointsUser.id 
                    ? { ...u, points: Math.max(0, u.points + amount) } 
                    : u
                ));
                toast.success(`${amount > 0 ? 'Added' : 'Removed'} ${Math.abs(amount)} points`);
                setAdjustPointsUser(null);
                setPointsAmount('');
                setPointsReason('');
              }}
            >
              Adjust Points
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Email Dialog */}
      <Dialog open={!!editEmailUser} onOpenChange={() => setEditEmailUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User Email</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>User</Label>
              <p className="text-sm font-medium text-slate-900 mt-2">{editEmailUser?.name}</p>
            </div>
            <div>
              <Label>Current Email</Label>
              <p className="text-sm text-slate-600 mt-2">{editEmailUser?.email}</p>
            </div>
            <div>
              <Label>New Email Address</Label>
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email"
                className="mt-2"
              />
            </div>
            <Button
              className="w-full"
              onClick={() => {
                if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
                  toast.error('Please enter a valid email address');
                  return;
                }
                setUsers(users.map(u => u.id === editEmailUser.id ? { ...u, email: newEmail } : u));
                toast.success('Email updated successfully');
                setEditEmailUser(null);
              }}
            >
              Update Email
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteUser}
        onOpenChange={() => setDeleteUser(null)}
        title="Delete User"
        description={`Are you sure you want to delete "${deleteUser?.name}"? This will permanently remove their account, reviews, and all associated data. This action cannot be undone.`}
        confirmLabel="Delete User"
        variant="destructive"
        onConfirm={() => {
          setUsers(users.filter(u => u.id !== deleteUser.id));
          toast.success('User deleted successfully');
          setDeleteUser(null);
        }}
      />
    </div>
  );
};